import React, { useState, useEffect } from 'react';
import Select from 'react-select';

import api from '../api';

import {scaleLinear, scaleSequential, scaleSqrt, scaleOrdinal, extent, min, max, maxIndex } from 'd3';
import {DotPlot} from './dotPlot/DotPlot'; 
import {DiffDotPlot} from './dotPlot/DiffDotPlot'; 
import {FaDetailPlot} from './dotPlot/FaDetailPlot'; 
import { useFattyAcidDetail } from './dotPlot/useFattyAcidDetail';
import { BarChart } from './barChart/BarChart'; 
import {LinePlot} from './linePlot/LinePlot'; 
import { DetailPlot } from './detailPlot/DetailPlot'; 
import { hslInterpolator } from './generalPlot/hslInterpolator';
import { generateSchemeHsl } from './generalPlot/schemeHsl';
import { useResults } from './useResults';
import { useResultJson } from './useResultJson';
import { useDomain } from './useDomain';
import { Alert } from './Alert';


const AccordionWrapper = ({status, title, panelId, first, children, key }) => {
  return (
    <div className={"accordion-item " + status} key={panelId}>
      <h2 className="accordion-header">
        <button 
          className="accordion-button" 
          type="button" 
          data-bs-toggle="collapse" // {i===0 ? "collapse" : "collapse collapsed"}
          data-bs-target={"#"+panelId}
          aria-expanded={first}
          aria-controls={panelId}>
          {title}
        </button>
      </h2>
      <div 
        id={panelId}
        className={first ? "accordion-collapse collapse show" : "accordion-collapse collapse"}
        data-bs-target="#accordion"
      >
      <div className="accordion-body">
        <article className="result" key={key}>
          <div className="container">
            {children}
          </div>
        </article>
      </div>
    </div>
  </div>
)};


export const Report = ({token, jobInfo, idx, first, alert}) => {
  const title=jobInfo.assay.name + " " + jobInfo.lipid_class_shorthand;
  const panelId = 'panel-' + jobInfo.id

  const [job, setJob] = useState({...jobInfo});
  const [pending, setPending] = useState(true);
  const [toggle, setToggle] = useState(false);
  const [cutoff, setCutoff] = useState(0.001);
  const [hoveredFa, setHoveredFa] = useState(null);
  const [lastFa, setLastFa] = useState(null);
  const [selectedLipid, setSelectedLipid] = useState(null);
  const [lastLipid, setLastLipid] = useState(null);
 
  const updateJob = async (jobId) => {
    const { data } = await api.get(
      '/info/job', {
        params: {
          job_id: jobId
        },
        headers: {
          authorization: token
        }
      }
    ).catch(error => console.log(error));
    return data;
  };

  useEffect(() => {
    if (!pending) {
      return;
    }
    if (token === null) return;
    const interval = setInterval(async () => {
      const updatedJob = await updateJob(jobInfo.id);
      if ((updatedJob.status !== 'pending') && (updatedJob.status !== 'queued')) {
        setPending(false)
        console.debug(jobInfo.id, updatedJob.id)
        setJob(updatedJob)
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [pending]);

  useEffect(() => (setPending(true)), [alert])

  // data
  const targetLipidProfile = useResults({route: '/result/target-lipid-profile', job, token});
  const resultLipidProfile = useResults({route: '/result/modeled-lipid-profile', job, token});
  const diffLipidProfile = useResults({route: '/result/diff-lipid-profile', job, token});
  const resultFaProfile = useResults({route: '/result/fatty-acid-profile', job, token});
  const historyFaProfile = useResults({route: '/result/fatty-acid-profile-history', job, token});
  const domain = useDomain({job, token});
  const molecularSpecies = useResultJson({route: '/result/molecular-species', job, token});
  const fattyAcidDetail = useFattyAcidDetail({job, token});

  if (pending || !targetLipidProfile || !resultLipidProfile || !domain || !resultFaProfile || !historyFaProfile || !molecularSpecies || !diffLipidProfile || !fattyAcidDetail) {
    if (!pending) setPending(true);
    return <AccordionWrapper status="pending" title={title} panelId={panelId} first={first}>
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </AccordionWrapper>
  }

  const joinedProfile = targetLipidProfile
    .concat(resultLipidProfile)
    .filter(d => d.probability > cutoff);
  
  const x = scaleLinear()
    .domain([Math.round(min(joinedProfile, d => d.cc) / 2) * 2 - 2, Math.round(max(joinedProfile, d => d.cc) / 2) * 2 + 2]);

  const y = scaleLinear()
    .domain([0, Math.round(max(joinedProfile, d => d.db) / 2) * 2 + 1]);
  
  const r = scaleSqrt()
    .domain([0, max(joinedProfile, d => d.probability)])
    .range([0, 20]);

  const color = scaleSequential(hslInterpolator)
    .domain(extent(joinedProfile, d => d.cc));

  const faYScale = scaleLinear()
    .domain([0, max(resultFaProfile.concat(historyFaProfile), d => d.probability)]);
  
  const faColor = scaleOrdinal()
              .range(generateSchemeHsl(20))
              .domain(domain);
  
  const availableLipids = Object.keys(molecularSpecies).sort();
  const detailLipid = selectedLipid || lastLipid ||  targetLipidProfile[maxIndex(targetLipidProfile, d=>d.probability)].lipid;
  const detailData = molecularSpecies[detailLipid] || [];
  const detailFa = hoveredFa || lastFa || resultFaProfile[maxIndex(resultFaProfile, d=>d.probability)].fatty_acid;
  const detailFaData = fattyAcidDetail[detailFa] || [];
  
  const _id = title.replaceAll(' ', '_') + '-';
  return (
    <AccordionWrapper status={job.status} title={title} panelId={panelId} first={first}>
      <Alert 
        status={job.status}
        error_pct={job.error.toLocaleString(undefined, {style: 'percent', minimumFractionDigits:2})}
      />
      <div className="row">
        <div className="col-xxl-3 col-lg-6" id={_id+'fatty_acid_profile'}>
          <BarChart
            data={resultFaProfile.filter(d => d.probability > cutoff)}
            width={400}
            height={400}
            color={faColor}
            onHover={setHoveredFa}
            hovered={hoveredFa}
            yScale={faYScale}
            title="Modeled fatty acid profile"
          />
        </div>
        <div className="col-xxl-3 col-lg-6" id={_id+'fatty_acid_profile_history'}>
          <LinePlot
            data={historyFaProfile.filter(d => d.probability > cutoff)}
            width={400}
            height={400}
            color={faColor}
            onHover={setHoveredFa}
            hovered={hoveredFa}
            yScale={faYScale}
            title="Optimization history"
          />
        </div>
        <div className="col-xxl-3 col-lg-6" id={_id+'target_lipid_profile'}>
          <DotPlot 
            data={targetLipidProfile.filter(d => d.probability > cutoff)}
            width={400}
            height={400}
            xScale={x}
            yScale={y}
            r={r}
            color={color}
            setSelectedLipid={setSelectedLipid}
            title="Experimental lipid profile"
          />
        </div>
        <div className="col-xxl-3 col-lg-6" id={_id + 'modeled_lipid_profile'}>
          <DotPlot 
            data={resultLipidProfile.filter(d => d.probability > cutoff)}
            width={400}
            height={400}
            xScale={x}
            yScale={y}
            r={r}
            color={color}
            setSelectedLipid={setSelectedLipid}
            title="Modeled lipid profile"
          />
        </div>
      </div>
      {toggle ?
      <>
        <hr />
        <div className="row align-items-end">
          <div className="col-xxl-3 col-lg-6" id={_id + `${detailFa.replace(' ', '_')}_contribution`}>
            <Select
              isSearchable
              isClearable
              options={resultFaProfile.map(d => ({value: d.fatty_acid, label: d.fatty_acid}))}
              onChange={fattyAcid => fattyAcid ? setLastFa(fattyAcid.value) : setLastFa(fattyAcid)}
              value={lastFa ? {value: lastFa, label: lastFa}: null}
              defaultValue={{value: detailFa, label: detailFa}}
            />
            <FaDetailPlot 
              data={detailFaData.filter(d => d.probability > cutoff)}
              width={400}
              height={400}
              xScale={x}
              yScale={y}
              r={r}
              color={faColor(detailFa)}
              setSelectedLipid={setSelectedLipid}
              title={`${detailFa} contribution`}
            />
          </div>
          <div className="col-xxl-3 col-lg-6" id={_id + `${detailLipid.replace(' ', '_')}_molecular_species`}>
            <Select
              isSearchable
              isClearable
              options={availableLipids.map(d => ({value: d, label: d}))}
              onChange={lipid => lipid ? setLastLipid(lipid.value) : setLastLipid(lipid)}
              value={lastLipid ? {value: lastLipid, label: lastLipid} : null}
            />
            <DetailPlot 
              data={detailData.filter(d => d.probability > cutoff)}
              width={400}
              height={400}
              color={faColor}
              title={`Molecular species of ${detailLipid}`}
            />
          </div>
          <div className="col-xxl-3 col-lg-6" id={_id + 'diff_lipid_profile'}>
            <DiffDotPlot 
              data={diffLipidProfile.filter(d => Math.abs(d.probability) > cutoff)}
              width={400}
              height={400}
              xScale={x}
              yScale={y}
              r={r}
              setSelectedLipid={setSelectedLipid}
              title="Differences of lipid profiles"
            />
          </div>
        </div>
      </>
      : null}
      <div className="row align-items-center">
        <div className="col-sm">
          <button className="btn btn-outline-secondary" type="button" onClick={() => setToggle(!toggle)}>
            {toggle ? "Hide detail plots" : "Show detail plots"}
          </button>
        </div>
        <div className="col-sm">
          <label htmlFor="cutoffRange" className="form-label">Abundance cutoff in %</label>
        </div>
        <div className="col-sm-7">
          <input 
            className="form-range" 
            type="range"
            id="cutoffRange"
            value={cutoff}
            min={0}
            max={0.05}
            step={0.0001}
            onChange={e => setCutoff(e.target.value)}
          />
        </div>
        <div className="col-sm">
          <input 
            className="form-control"
            type="number"
            value={cutoff*100}
            min={0}
            max={5}
            step={0.2}
            onChange={e => setCutoff(e.target.value/100)}
            />
        </div>
      </div>
    </AccordionWrapper>
  )
};


